import React, { useState } from 'react'
import MDModal from 'components/MDModal'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import MDButton from 'components/MDButton'
import MDInput from 'components/MDInput'
import { Divider, Grid, Stack } from '@mui/material'
import { useAppServices } from 'hook/services'
import InvoiceModal from '../InvoiceModal'

const DiscountModal = ({ open, onClose, booking, onSuccess }) => {
  const AppService = useAppServices()
  const [discountPercentage, setDiscountPercentage] = useState('')
  const [processing, setProcessing] = useState(false)
  const [invoiceOpen, setInvoiceOpen] = useState(false)
  const [invoiceData, setInvoiceData] = useState(null)

  const basePrice = parseFloat(booking?.bookingPrice || 0)
  const discount = Math.min(100, Math.max(0, parseFloat(discountPercentage) || 0))
  const discountAmount = parseFloat(((basePrice * discount) / 100).toFixed(2))
  const finalPrice = parseFloat((basePrice - discountAmount).toFixed(2))

  const handleApply = async () => {
    if (!discount) { alert('Please enter a discount percentage.'); return }
    if (!window.confirm(`Apply ${discount}% discount? Final amount will be $${finalPrice.toFixed(2)}.`)) return

    setProcessing(true)
    try {
      const payload = {
        _id: booking._id,
        discountPercentage: discount,
        discountAmount,
        finalPrice,
      }
      const { response } = await AppService.booking.applyDiscount({ payload })
      if (response?.success) {
        const inv = {
          bookingNo: response?.data?.bookingNo || booking?.bookingNo || 'N/A',
          customerName: `${booking?.firstName || ''} ${booking?.lastName || ''}`.trim(),
          company: booking?.company || '',
          listing: booking?.shootDetail?.listing?.title || '',
          shootDate: booking?.shootDetail?.shootDate
            ? new Date(booking.shootDetail.shootDate).toLocaleDateString('en-GB')
            : '',
          startTime: booking?.shootDetail?.startTime || '',
          endTime: booking?.shootDetail?.endTime || '',
          basePrice,
          discountPercentage: discount,
          discountAmount,
          finalPrice: response?.data?.finalPrice ?? finalPrice,
          invoiceUrl: response?.data?.invoiceUrl || null,
          serviceCharges: parseFloat(booking?.serviceCharges || 0),
          taxPrice: parseFloat(booking?.taxPrice || 0),
          createdAt: new Date().toLocaleDateString(),
        }
        setInvoiceData(inv)
        setInvoiceOpen(true)
        if (onSuccess) onSuccess()
        onClose()
      } else {
        alert(response?.message || 'Failed to apply discount.')
      }
    } catch (err) {
      console.error(err)
      alert('Something went wrong.')
    }
    setProcessing(false)
  }

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 520,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
  }

  return (
    <>
      <MDModal open={open} onClose={onClose}>
        <MDBox sx={style}>
          <MDTypography variant="h5" fontWeight="bold" mb={1}>
            Add Discount
          </MDTypography>
          <MDTypography variant="body2" color="text" mb={2}>
            Booking: <strong>{booking?.bookingNo}</strong> &nbsp;|&nbsp; Customer:{' '}
            <strong>{`${booking?.firstName || ''} ${booking?.lastName || ''}`.trim()}</strong>
          </MDTypography>

          <MDTypography variant="body2" color="text" mb={2}>
            WithoutDiscountPrice: <strong>{booking?.withoutDiscountPrice}</strong>
          </MDTypography>

          <Divider />

          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <MDInput
                type="number"
                label="Discount (%)"
                value={discountPercentage}
                onChange={(e) => setDiscountPercentage(e.target.value)}
                fullWidth
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>

            {discountPercentage !== '' && (
              <Grid item xs={12}>
                <MDBox
                  p={2}
                  sx={{
                    background: '#e3f2fd',
                    borderRadius: 2,
                    border: '1px solid #90caf9',
                  }}
                >
                  <Stack spacing={0.5}>
                    <MDTypography variant="body2">
                      Current Booking Price: <strong>${basePrice.toFixed(2)}</strong>
                    </MDTypography>
                    <MDTypography variant="body2" sx={{ color: '#d32f2f' }}>
                      Discount ({discount}%): <strong>-${discountAmount.toFixed(2)}</strong>
                    </MDTypography>
                    <Divider sx={{ my: 0.5 }} />
                    <MDTypography variant="body1" fontWeight="bold" sx={{ color: '#1b5e20' }}>
                      Final Total (100% Due): ${finalPrice.toFixed(2)}
                    </MDTypography>
                  </Stack>
                </MDBox>
              </Grid>
            )}
          </Grid>

          <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
            <MDButton variant="gradient" color="secondary" onClick={onClose} disabled={processing}>
              Cancel
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleApply}
              disabled={processing || !discountPercentage}
            >
              {processing ? 'Applying...' : 'Apply'}
            </MDButton>
          </Stack>
        </MDBox>
      </MDModal>


    </>
  )
}

export default DiscountModal
